import { DateTime } from 'luxon'

import {
	PaymentTokenDTO,
	PaymentInfoDTO,
	CardInfoDTO,
	SepaInfoDTO,
} from '@/core/api/dtos/response/payment/paymentToken'
import {
	PaymentToken,
	PaymentInfo,
	CardInfo,
	SepaInfo,
} from '@/core/api/entities/paymentToken'

export class PaymentTokenMapper {
	static toEntity(dto: PaymentTokenDTO): PaymentToken {
		// Determine type based on internal_type or data.is_card
		const type: 'card' | 'sepa' =
			dto.internal_type === 'credit_card' || dto.data?.is_card === true
				? 'card'
				: 'sepa'

		// Extract payment info from the new structure
		let paymentInfo: PaymentInfo | undefined

		if (type === 'card' && dto.data?.payment_info) {
			const cardInfo = dto.data.payment_info
			paymentInfo = new PaymentInfo({
				card: new CardInfo({
					brand: cardInfo.brand || cardInfo.display_brand || '',
					last4: cardInfo.last4 || '',
					expMonth: cardInfo.exp_month || 0,
					expYear: cardInfo.exp_year || 0,
				}),
			})
		} else if (type === 'sepa') {
			// For SEPA, we might need to extract from different fields
			const sepaData = dto.data
			paymentInfo = new PaymentInfo({
				sepa: new SepaInfo({
					iban:
						sepaData?.iban ||
						(sepaData?.payment_info?.last4
							? `**** ${sepaData.payment_info.last4}`
							: ''),
					accountHolderName: '',
					accountHolderEmail: sepaData?.customer_email || '',
				}),
			})
		}

		return new PaymentToken({
			id: dto.id,
			accountId: dto.account_id,
			type,
			isDefault: dto.is_default,
			active: dto.active,
			expiresAt: dto.expires_at ? DateTime.fromISO(dto.expires_at) : null,
			createdAt: DateTime.fromISO(dto.created_at),
			updatedAt: DateTime.fromISO(dto.updated_at),
			paymentInfo,
		})
	}

	static toEntities(dtos: PaymentTokenDTO[]): PaymentToken[] {
		return dtos.map((dto) => PaymentTokenMapper.toEntity(dto))
	}

	static toPaymentInfo(dto: PaymentInfoDTO): PaymentInfo {
		return new PaymentInfo({
			card: dto.card ? PaymentTokenMapper.toCardInfo(dto.card) : undefined,
			sepa: dto.sepa ? PaymentTokenMapper.toSepaInfo(dto.sepa) : undefined,
		})
	}

	static toCardInfo(dto: CardInfoDTO): CardInfo {
		return new CardInfo({
			brand: dto.brand,
			last4: dto.last4,
			expMonth: dto.exp_month,
			expYear: dto.exp_year,
		})
	}

	static toSepaInfo(dto: SepaInfoDTO): SepaInfo {
		return new SepaInfo({
			iban: dto.iban,
			accountHolderName: dto.account_holder_name,
			accountHolderEmail: dto.account_holder_email,
		})
	}
}
